import { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import { Button } from "@/components/ui/button";

interface IssueResponse {
  stateId: string;
  pickerUrl: string;
  expiresAt: string;
}

interface ResultResponse {
  status: "pending" | "selected" | "cancelled" | "failed";
  appBaseFolder?: string;
}

export function AppBaseFolderPickerButton(props: {
  storageId: string;
  appBindingId?: string;
  suggestedPath: string;
  onSelect: (path: string) => void;
}) {
  const [stateId, setStateId] = useState<string | null>(null);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!stateId) return;
    let stopped = false;
    const timer = window.setInterval(() => {
      void apiClient<ResultResponse>(`/console-bff/app-base-picker/${stateId}/result`).then((result) => {
        if (stopped) return;
        if (result.status === "cancelled") {
          setError("フォルダ選択を取り消しました。");
          setStateId(null);
          return;
        }
        if (result.status === "failed") {
          setError("選択結果を反映できませんでした。Storageやアプリ設定を確認して、もう一度選択してください。");
          setStateId(null);
          return;
        }
        if (result.status !== "selected" || !result.appBaseFolder) return;
        props.onSelect(result.appBaseFolder);
        setStateId(null);
        setFallbackUrl(null);
        setError(null);
      }).catch((failure: unknown) => {
        if (!stopped) {
          setError(failure instanceof Error ? failure.message : "選択結果を確認できませんでした。");
          setStateId(null);
        }
      });
    }, 1500);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [stateId, props.onSelect]);

  async function start() {
    if (!props.storageId || busy) return;
    const tab = window.open("about:blank", "_blank");
    setBusy(true);
    setError(null);
    try {
      const result = await apiClient<IssueResponse>("/console-bff/app-base-picker", {
        method: "POST",
        body: JSON.stringify({
          storageId: props.storageId,
          ...(props.appBindingId ? { appBindingId: props.appBindingId } : {}),
          initialRef: "/",
          suggestedPath: props.suggestedPath || "/",
        }),
      });
      setStateId(result.stateId);
      if (tab) {
        tab.opener = null;
        tab.location.assign(result.pickerUrl);
      } else {
        setFallbackUrl(result.pickerUrl);
      }
    } catch (failure) {
      tab?.close();
      setError(failure instanceof Error ? failure.message : "フォルダ選択画面を開けませんでした。");
    } finally {
      setBusy(false);
    }
  }

  return <div className="space-y-2">
    <Button type="button" variant="outline" disabled={!props.storageId || busy} onClick={() => void start()}>
      {busy ? "開始中…" : "フォルダを選ぶ"}
    </Button>
    {stateId && <p role="status" className="text-xs text-muted-foreground">
      別タブでフォルダを選択してください。選択結果を確認しています。
    </p>}
    {fallbackUrl && <a href={fallbackUrl} target="_blank" rel="noopener noreferrer" className="text-sm underline">
      フォルダ選択画面を開く
    </a>}
    {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
  </div>;
}
